import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export default function PrivacyPolicyPage() {
  useDocumentMeta(
    'Privacy Policy',
    'Privacy Policy for Takedown League. Learn what information we collect, how we use it, and your choices regarding cookies, analytics, and advertising.'
  );

  return (
    <PublicLayout>
      <div className="bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-6">Last updated: February 10, 2026</p>

          <div className="space-y-6 text-gray-700 text-sm leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Overview</h2>
              <p>
                Takedown League ("we", "us", or "our") operates the website at takedownleague.com.
                This page describes what information we collect, how we use it, and your choices
                regarding that information.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Information We Collect</h2>
              <p className="mb-2">When you use Takedown League, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Account information:</strong> Email address and display name you provide
                  when registering.
                </li>
                <li>
                  <strong>Usage data:</strong> Pages visited, features used, and interactions within
                  the app, collected through Google Analytics.
                </li>
                <li>
                  <strong>Device information:</strong> Browser type, operating system, and screen
                  size, collected automatically through analytics.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Cookies and Tracking</h2>
              <p className="mb-2">We use cookies and similar technologies for:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Authentication:</strong> Keeping you logged in during your session.
                </li>
                <li>
                  <strong>Analytics:</strong> We use Google Analytics 4 to understand how visitors
                  use our site. Google Analytics collects information such as how often users visit
                  the site, what pages they visit, and what other sites they used before coming to
                  ours. Google's ability to use and share information collected by Google Analytics
                  is governed by the{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    Google Privacy Policy
                  </a>
                  .
                </li>
                <li>
                  <strong>Advertising:</strong> We use Google AdSense to display advertisements.
                  Google and its partners may use cookies to serve ads based on your prior visits to
                  this site or other websites. You can opt out of personalized advertising by
                  visiting{' '}
                  <a
                    href="https://www.google.com/settings/ads"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    Google Ads Settings
                  </a>
                  .
                </li>
              </ul>
            </section>

            {/* Ad Banner - Mid-policy placement */}
            <div className="my-8">
              <AdBanner slot={AD_SLOTS.privacyPolicy} format="horizontal" />
            </div>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Third-Party Services</h2>
              <p>We use the following third-party services that may collect data:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>
                  <strong>Amazon Web Services (AWS):</strong> Hosting, authentication, and data
                  storage.
                </li>
                <li>
                  <strong>Google Analytics:</strong> Website usage analytics.
                </li>
                <li>
                  <strong>Google AdSense:</strong> Advertisement display and management.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                How We Use Your Information
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To provide and maintain the Takedown League service.</li>
                <li>To identify you within your leagues and display your standings.</li>
                <li>To analyze usage patterns and improve the site.</li>
                <li>To display relevant advertisements.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Retention</h2>
              <p>
                We retain your account information for as long as your account is active. Analytics
                data is retained according to Google's standard retention policies. You can request
                deletion of your account and associated data by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Your Choices</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>You can disable cookies in your browser settings.</li>
                <li>
                  You can opt out of Google Analytics by installing the{' '}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    Google Analytics Opt-out Browser Add-on
                  </a>
                  .
                </li>
                <li>
                  You can opt out of personalized ads at{' '}
                  <a
                    href="https://www.google.com/settings/ads"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    Google Ads Settings
                  </a>
                  .
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Children's Privacy</h2>
              <p>
                Takedown League is not directed at children under 13. We do not knowingly collect
                personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. Changes will be posted on this
                page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Contact</h2>
              <p>
                If you have questions about this privacy policy, please contact us through our{' '}
                <a
                  href="https://takedownleague.tawk.help/category/getting-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 underline"
                >
                  Help Center
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
